// Error-handling policy that more than one of `app-sentry.ts`'s entry points needs.
//
// Kept out of `app-sentry.ts` so it can be unit-tested cheaply: anything here stays free of
// Angular and of the Sentry SDK, so a test loads this module on its own with no setup at all.
// (Since df-106e22b8ff13 `app-sentry.ts` can be loaded by a test as well, but only through the
// node environment helper, because its import graph reaches both of those.)

import { describeErrorForLog } from '@cf-shared/error-describe';
import { DatabaseUnavailableError, isDatabaseShuttingDown } from 'services/database-unavailable-error';

/** The part of `DatabaseRecoveryService` this policy uses. Typed structurally rather than
 * importing the service, which pulls in Angular and cannot load in a unit test.
 * `DatabaseRecoveryService` satisfies it. */
export interface IntentionalDestructionSource {
  isIntentionalDestruction(): boolean;
}

/** The part of `LoggingService` this policy uses. Typed structurally for the same reason.
 * `LoggingService` satisfies it. */
export interface DatabaseUnavailableReporter {
  trackSilentProblem(problemName: string, context: any): void;
  trackSilentInfo(infoName: string, context: any): void;
}

/** What to do about a read that refused because the local database was not available (deferred
 * #2 — every `DatabaseWrapper` read now throws rather than answering with an empty result).
 *
 * Three different situations produce this error, and they get different treatment, mirroring
 * what `interceptDatabaseError` does for the IndexedDB case:
 *
 *  - DURING our own teardown, once the sign-out WIPE FLAG is up: expected, not a fault.
 *    `console.log` only — no Sentry event, and deliberately not `console.error`, so it cannot
 *    trip the E2E unexpected-browser-error gate.
 *  - A SHUTDOWN HAS BEGUN but that flag is not up yet: also our own doing, but worth a record.
 *    This is the ordinary sign-out window, and it is wider than it looks — `app.component.ts`
 *    shuts every wrapper down when the auth state goes to logged-out, and only much later does
 *    the login page trigger `reloadAppAsync`, which raises the wipe flag. Anything still
 *    reading or writing in between refuses here. It goes through `trackSilentInfo`, so Sentry
 *    keeps a searchable record of WHICH caller kept working while the database closed, at info
 *    level, with a `console.log` line rather than a `console.error` (df-0c444ca6bb2a).
 *
 *    Why the info tier and not the problem tier, since an unaudited caller is arguably
 *    something to act on: the refusal itself is correct behaviour — we closed that database on
 *    purpose — so it is the rare-but-expected observation `trackSilentInfo` is for. What it may
 *    say about the caller is still in the event, in the stack this report carries. Filing it as
 *    a problem instead is what made every ordinary sign-out write a `console.error` and turn
 *    E2E runs red at random.
 *  - OTHERWISE: realistically a transient IndexedDB recovery (iOS backgrounding), or a missing
 *    database handle — early boot, a wrapper re-created after the sign-out, or the window after
 *    a purge or restore destroyed the store. The user can do nothing about it and it resolves
 *    itself, so no toast — but it is worth a record, so it goes through `trackSilentProblem`.
 *    That DOES emit its own `console.error` line, so an E2E scenario hitting this window needs
 *    `DatabaseUnavailableUnhandled` in its ignore list — a sign-out scenario included, because
 *    a transient refusal can still land in one.
 *
 * `isDatabaseShuttingDown` is the test for the middle case, NOT `isDatabaseTornDown`: the wider
 * one also counts `NoDatabaseHandle`, which happens at early boot as well as during a teardown,
 * and quietening that would hide a genuine start-up failure. The helper says so where it is
 * defined.
 *
 * Whatever is reported carries `describeErrorForLog(error)` as well as the reason and the
 * database, because the report is the ONLY thing that can name the caller whose read or write was
 * refused (df-643be5ff4818). `trackSilentProblem` goes through Sentry's `captureMessage`, which
 * attaches no stack of its own, and the `unhandledrejection` listener calls `preventDefault()`, so
 * the browser prints no stack either — without this field, nothing anywhere says who raced. Note
 * that Sentry symbolicates an exception's own stack trace and NOT a stack inside a context field,
 * so a production event shows minified frames; source maps are shipped, so they are still
 * resolvable. This field reaches an E2E browser log as well as Sentry, because `trackSilentReport`
 * puts the context on its console line as single-line text rather than as an object argument — that
 * log would have rendered an object argument as the literal `[object Object]` (df-2ff64a6cfd69).
 *
 * `recovery` and `reporter` are both null when the error arrives before `SentryErrorHandler`'s
 * constructor has run and made the two services available — early boot is exactly when a
 * no-database-handle refusal is plausible. That case still writes a line, because this must never
 * suppress into total silence. They are two arguments rather than one because they are two
 * services: whether this is our own teardown is database recovery's answer, and filing the report
 * is the logging service's job (df-0630dafeff8d).
 *
 * Both of the entry points that see this error call this, so the split above is written once. It
 * used to be written out twice, kept in step only by a comment saying "same split as the
 * unhandledrejection listener above" (deferred finding df-57f1a22751fe). */
export function reportDatabaseUnavailableError(
  error: DatabaseUnavailableError,
  recovery: IntentionalDestructionSource | null,
  reporter: DatabaseUnavailableReporter | null,
): void {
  // if the sign-out wipe flag is already up, this is expected and gets no Sentry event
  if (recovery?.isIntentionalDestruction()) {
    console.log('Suppressed database-unavailable error caused by our own teardown:', error.message);
    return;
  }
  const context = {
    reason: error.reason,
    database: error.databaseIdentifier,
    error: describeErrorForLog(error),
  };
  // if a shutdown had already begun, this is our own sign-out and is recorded at the quiet tier
  if (isDatabaseShuttingDown(error)) {
    if (reporter) {
      reporter.trackSilentInfo('DatabaseUnavailableDuringTeardown', context);
    } else {
      // console.log, not console.error: a shutdown refusal is not a fault at any point in boot
      console.log('Database refused during teardown, before logging was ready:', describeErrorForLog(error));
    }
    return;
  }
  // if logging is available, record it quietly; otherwise at least say something
  if (reporter) {
    reporter.trackSilentProblem('DatabaseUnavailableUnhandled', context);
  } else {
    console.error('Database unavailable before logging was ready:', describeErrorForLog(error));
  }
}
